import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://art-fluence-backend.vercel.app/api';

const AdminNewArrivals = () => {
  const navigate = useNavigate();
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    image: '',
    name: '',
    size: '',
    EHP: '',
    medium: '',
    artist: '',
    price: '',
    url: '',
    hours: '24'
  });

  useEffect(() => {
    fetchArrivals();
  }, []);

  const fetchArrivals = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/new-arrivals/all`);
      const data = await response.json();
      setArrivals(data);
    } catch (error) {
      console.error('Error fetching arrivals:', error);
      alert('Error fetching new arrivals');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId 
        ? `${API_BASE_URL}/new-arrivals/${editingId}`
        : `${API_BASE_URL}/new-arrivals`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save new arrival');
      }

      await fetchArrivals();
      resetForm();
      alert(editingId ? 'New arrival updated successfully!' : 'New arrival created successfully!');
    } catch (error) {
      console.error('Error saving arrival:', error);
      alert('Error saving new arrival');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (arrival) => {
    setEditingId(arrival._id);
    const hoursRemaining = Math.ceil((new Date(arrival.expiresAt) - new Date()) / (1000 * 60 * 60));
    setFormData({
      image: arrival.image,
      name: arrival.name,
      size: arrival.size,
      EHP: arrival.EHP,
      medium: arrival.medium,
      artist: arrival.artist,
      price: arrival.price,
      url: arrival.url,
      hours: hoursRemaining > 0 ? hoursRemaining.toString() : '24'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this new arrival?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/new-arrivals/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete new arrival');
      }

      await fetchArrivals();
      alert('New arrival deleted successfully!');
    } catch (error) {
      console.error('Error deleting arrival:', error);
      alert('Error deleting new arrival');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      image: '',
      name: '',
      size: '',
      EHP: '',
      medium: '',
      artist: '',
      price: '',
      url: '',
      hours: '24'
    });
    setEditingId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            ← Back
          </button>
          <h1 className="text-4xl font-serif text-gray-800 mb-2">Manage New Arrivals</h1>
          <p className="text-gray-600">Add, edit, or delete new arrival items with automatic expiration</p>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {editingId ? 'Edit New Arrival' : 'Add New Arrival'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL *
                </label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="Artwork Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Size *
                </label>
                <input
                  type="text"
                  name="size"
                  value={formData.size}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="27 x 45 Inches"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EHP *
                </label>
                <input
                  type="text"
                  name="EHP"
                  value={formData.EHP}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="EHP 301"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medium *
                </label>
                <input
                  type="text"
                  name="medium"
                  value={formData.medium}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="Acrylic on Canvas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Artist *
                </label>
                <input
                  type="text"
                  name="artist"
                  value={formData.artist}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="Artist Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price *
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="₹ 61000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  View Details URL *
                </label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="/exclusivehandpaintings"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires In (Hours) *
                </label>
                <input
                  type="number"
                  name="hours"
                  value={formData.hours}
                  onChange={handleInputChange}
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="24"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Item will be automatically removed after this many hours
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold mb-4">All New Arrivals</h2>
          {loading && arrivals.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Loading...</p>
          ) : arrivals.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No new arrivals found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">EHP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artist</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {arrivals.map((arrival) => (
                    <tr key={arrival._id} className={isExpired(arrival.expiresAt) ? 'bg-red-50' : ''}>
                      <td className="px-4 py-3">
                        <img
                          src={arrival.image}
                          alt={arrival.name}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/64';
                          }}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{arrival.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{arrival.EHP}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{arrival.artist}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{arrival.price}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{formatDate(arrival.expiresAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          isExpired(arrival.expiresAt)
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {isExpired(arrival.expiresAt) ? 'Expired' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleEdit(arrival)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(arrival._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNewArrivals;

